/**
 * Network Manager - WebSocket and HTTP handling
 */

import { EventEmitter } from '../core/events.js';

export class NetworkManager extends EventEmitter {
  constructor(client) {
    super();
    this.client = client;
    this.ws = null;
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.messageId = 0;
    this.pendingRequests = new Map();
  }

  /**
   * Connect WebSocket
   */
  connect(url) {
    return new Promise((resolve, reject) => {
      const wsUrl = url.replace(/^http/, 'ws') + '/ws';

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.client._log('WebSocket connected');
        this._startHeartbeat();
        resolve();
      };

      this.ws.onmessage = (event) => {
        this._handleMessage(event.data);
      };

      this.ws.onclose = () => {
        this.client._log('WebSocket disconnected');
        this._stopHeartbeat();
        this.emit('disconnected');
      };

      this.ws.onerror = (error) => {
        this.client._log('WebSocket error:', error);
        reject(error);
      };
    });
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    this._stopHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Authenticate via WebSocket
   */
  authenticate(userId, token) {
    return this._sendRequest({
      type: 'auth',
      payload: {
        user_id: userId,
        token: token,
        device_id: this._getDeviceId(),
      },
    });
  }

  /**
   * Send WebSocket message
   */
  send(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    const msg = {
      id: this._generateMessageId(),
      timestamp: Date.now(),
      ...message,
    };

    this.ws.send(JSON.stringify(msg));
    return msg.id;
  }

  /**
   * Send request and wait for response
   */
  _sendRequest(message) {
    return new Promise((resolve, reject) => {
      const id = this._generateMessageId();
      const msg = {
        id,
        timestamp: Date.now(),
        ...message,
      };

      this.pendingRequests.set(id, { resolve, reject });

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('Request timeout'));
        }
      }, 10000);

      this.ws.send(JSON.stringify(msg));
    });
  }

  /**
   * HTTP GET request
   */
  async apiGet(path) {
    const response = await fetch(this.client.config.apiUrl + path, {
      headers: {
        'Authorization': this.client.state.token || '',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * HTTP POST request
   */
  async apiPost(path, data) {
    const response = await fetch(this.client.config.apiUrl + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.client.state.token || '',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Handle incoming WebSocket message
   */
  _handleMessage(data) {
    try {
      const msg = JSON.parse(data);

      // Handle response
      if (msg.id && this.pendingRequests.has(msg.id)) {
        const { resolve, reject } = this.pendingRequests.get(msg.id);
        this.pendingRequests.delete(msg.id);

        if (msg.error) {
          reject(new Error(msg.error));
        } else {
          resolve(msg.payload);
        }
        return;
      }

      // Emit message event
      this.emit('message', msg);

      // Handle specific message types
      switch (msg.type) {
        case 'chat':
          this.client.messages._handleIncomingMessage(msg);
          break;
        case 'delivery_receipt':
          this.client.messages._handleDeliveryReceipt(msg);
          break;
        case 'presence':
          this.emit('presence', msg.payload);
          break;
        case 'heartbeat_ack':
          // Heartbeat acknowledged
          break;
        case 'error':
          this.emit('error', new Error(msg.payload?.message || 'Unknown error'));
          break;
      }
    } catch (error) {
      this.client._log('Failed to parse message:', error);
    }
  }

  /**
   * Start heartbeat
   */
  _startHeartbeat() {
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({
          type: 'heartbeat',
          payload: {
            sequence: this.messageId,
            timestamp: Date.now(),
          },
        });
      }
    }, this.client.config.heartbeatInterval);
  }

  /**
   * Stop heartbeat
   */
  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  _generateMessageId() {
    return `msg_${++this.messageId}_${Date.now()}`;
  }

  _getDeviceId() {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'web_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  }
}
